import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { AppUser } from "@/lib/types";
import { logAudit } from "@/lib/storage";

interface AuthCtx {
  user: AppUser | null;
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  signup: (name: string, email: string, password: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

async function buildAppUser(session: Session | null): Promise<AppUser | null> {
  if (!session?.user) return null;
  const uid = session.user.id;
  const [{ data: profile }, { data: roles }] = await Promise.all([
    supabase.from("profiles").select("id, email, name, deleted_at").eq("id", uid).maybeSingle(),
    supabase.from("user_roles").select("role").eq("user_id", uid),
  ]);
  // Soft-deleted accounts: sign the session out immediately.
  if (profile?.deleted_at) {
    await supabase.auth.signOut();
    return null;
  }
  const isAdmin = (roles ?? []).some((r) => r.role === "admin");
  return {
    id: uid,
    email: profile?.email ?? session.user.email ?? "",
    name: profile?.name ?? session.user.user_metadata?.name ?? "",
    role: isAdmin ? "admin" : "member",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe first, then fetch initial session
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      // Defer to avoid deadlock with supabase callbacks
      setTimeout(() => {
        buildAppUser(s).then((u) => setUser(u));
      }, 0);
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      buildAppUser(data.session).then((u) => {
        setUser(u);
        setLoading(false);
      });
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  };

  const signup = async (name: string, email: string, password: string) => {
    // Land on the dashboard right after email confirmation instead of marketing home.
    const redirectUrl = `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl, data: { name } },
    });
    if (error) return { error: error.message };
    return {};
  };

  const logout = async () => {
    if (user) await logAudit(user, "logout");
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  return (
    <Ctx.Provider value={{ user, session, loading, login, signup, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
};
